import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function CandidateMatchedJobs() {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [minScore, setMinScore] = useState(0);
  const [viewBy, setViewBy] = useState('candidate');
  const [matchedJobs, setMatchedJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/v1/leads/?page=1&search=')
      .then(res => {
        setCandidates(res.data.results || []);
      });
  }, []);

  useEffect(() => {
    fetchMatchedJobs();
    // eslint-disable-next-line
  }, [selectedCandidate, fromDate, toDate, minScore, viewBy]);

  const fetchMatchedJobs = () => {
    setLoading(true);
    let url = '/scored-job?';
    if (selectedCandidate) url += `leadId=${encodeURIComponent(selectedCandidate)}&`;
    if (fromDate) url += `startDate=${encodeURIComponent(fromDate)}&`;
    if (toDate) url += `endDate=${encodeURIComponent(toDate)}&`;
    url = url.replace(/&$/, '');
    axios.get(url)
      .then(res => {
        let jobs = res.data.results || [];
        if (minScore > 0) {
          jobs = jobs.filter(j => (j.score || 0) >= minScore);
        }
        setMatchedJobs(jobs);
        setLoading(false);
      })
      .catch(() => {
        setMatchedJobs([]);
        setLoading(false);
      });
  };

  return (
    <div className="w-full max-w-6xl mx-auto mt-8">
      <h1 className="text-4xl font-bold mb-8">Job-Candidate Match Scores</h1>
      <div className="bg-white rounded-xl shadow p-8 flex flex-col gap-8">
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
            <div className="flex flex-col">
              <label className="text-lg font-semibold text-gray-600 mb-2">Candidate</label>
              <select value={selectedCandidate} onChange={e => setSelectedCandidate(e.target.value)} className="border rounded-lg px-4 py-3 text-xl text-gray-700 bg-gray-50 focus:outline-none">
                <option value="">All Candidates</option>
                {candidates.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label className="text-lg font-semibold text-gray-600 mb-2">From Date</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-4 py-3 text-xl text-gray-700 bg-gray-50 focus:outline-none" placeholder="Select start date" />
            </div>
            <div className="flex flex-col">
              <label className="text-lg font-semibold text-gray-600 mb-2">To Date</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-4 py-3 text-xl text-gray-700 bg-gray-50 focus:outline-none" placeholder="Select end date" />
            </div>
            <div className="flex flex-col">
              <label className="text-lg font-semibold text-gray-600 mb-2">Minimum Score: <span>{minScore}</span></label>
              <input type="range" min="0" max="1" step="0.01" value={minScore} onChange={e => setMinScore(Number(e.target.value))} className="w-full accent-blue-500" />
            </div>
          </div>
          <div className="mt-6 flex items-center gap-8">
            <span className="text-lg font-semibold text-gray-600">View By:</span>
            <label className="flex items-center gap-2 text-lg font-medium">
              <input type="radio" name="viewby" value="candidate" checked={viewBy === 'candidate'} onChange={() => setViewBy('candidate')} className="accent-blue-500" />
              <span>Candidate</span>
            </label>
            <label className="flex items-center gap-2 text-lg font-medium">
              <input type="radio" name="viewby" value="job" checked={viewBy === 'job'} onChange={() => setViewBy('job')} className="accent-blue-500" />
              <span>Job</span>
            </label>
          </div>
        </div>
      </div>
      {/* Candidate summary */}
      {selectedCandidate && candidates.length > 0 && (
        <div className="bg-indigo-50 rounded-t-xl px-8 py-6 mb-0">
          <div className="text-2xl font-bold text-indigo-700 mb-1">
            {candidates.find(c => c.id === selectedCandidate)?.name || ''}
          </div>
          <div className="text-lg text-blue-600 font-medium">
            {matchedJobs.length} matched jobs
          </div>
        </div>
      )}
      <div className="bg-white rounded-b-xl shadow">
        <table className="min-w-full text-left">
          <thead>
            <tr className="border-b">
              <th className="py-4 px-6 text-lg font-bold text-gray-500">JOB</th>
              <th className="py-4 px-6 text-lg font-bold text-gray-500">SCORE</th>
              <th className="py-4 px-6 text-lg font-bold text-gray-500">DATE</th>
              <th className="py-4 px-6 text-lg font-bold text-gray-500">MATCHED KEYWORDS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="py-8 px-6 text-center text-gray-400 text-xl">Loading...</td></tr>
            ) : matchedJobs.length > 0 ? (
              matchedJobs.map((row, idx) => {
                const jobTitle = row.jobTitle || 'AML Subject Matter Expert (SME)';
                const jobCompany = row.company || 'Oscilar';
                const jobLocation = row.location || 'United States';
                const score = row.score ? Math.round(row.score * 100) : 0;
                const date = row.generatedAt ? row.generatedAt.split(' ')[0] : '';
                const matchedKeywords = row.matchedKeywords || '';
                return (
                  <tr key={idx} className="border-b">
                    <td className="py-4 px-6 align-middle">
                      <div className="font-semibold text-lg text-black">{jobTitle}</div>
                      <div className="text-gray-500 text-md">{jobCompany} &bull; {jobLocation}</div>
                    </td>
                    <td className="py-4 px-6 align-middle">
                      <span className="bg-green-100 text-green-700 px-4 py-1 rounded-full font-bold text-lg">{score}%</span>
                    </td>
                    <td className="py-4 px-6 align-middle text-gray-500 text-lg">{date}</td>
                    <td className="py-4 px-6 align-middle text-gray-700 text-lg">{matchedKeywords}</td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={4} className="py-8 px-6 text-center text-gray-400 text-xl">No matched jobs found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
